import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import PartnerRibbon from "@/components/site/landing/PartnerRibbon";
import LandingHeader from "@/components/site/landing/LandingHeader";
import Hero from "@/components/site/landing/Hero";
import QuickBookCard from "@/components/site/landing/QuickBookCard";
import TrustBar from "@/components/site/landing/TrustBar";
import ToursSection from "@/components/site/landing/ToursSection";
import HowItWorks from "@/components/site/landing/HowItWorks";
import FleetSection from "@/components/site/landing/FleetSection";
import TestimonialsSection from "@/components/site/landing/TestimonialsSection";
import JoinSection from "@/components/site/landing/JoinSection";
import FinalCTA from "@/components/site/landing/FinalCTA";
import LandingFooter from "@/components/site/landing/LandingFooter";
import BookingModal from "@/components/site/landing/BookingModal";
import JoinModal from "@/components/site/landing/JoinModal";
import { API, JOIN_OPTIONS } from "@/components/site/landing/constants";
import "./landing.css";

/**
 * DiTours public landing — thin orchestrator. Owns global state (tours, fleet,
 * partner context, modals) and delegates rendering to per-section components in
 * /components/site/landing/.
 */
export default function Landing() {
  const [tours, setTours] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [partnerCtx, setPartnerCtx] = useState(null); // { slug, company_name } — set via ?partner= or subdomain
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingCategory, setBookingCategory] = useState("executive");
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [joinOpen, setJoinOpen] = useState(null); // "partner" | "driver" | "fleet"
  const { user } = useAuth();
  const { lang } = useLang();
  const isClient = user?.role === "client";

  useEffect(() => {
    axios.get(`${API}/public/tours?lang=${lang}`).then((r) => setTours(r.data || [])).catch(() => {});
    axios.get(`${API}/public/fleet?lang=${lang}`).then((r) => setFleet(r.data || [])).catch(() => {});
  }, [lang]);

  // Detect ?partner=slug (white-label hotel/agency) or a partner subdomain
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get("partner");
    const host = window.location.hostname;
    const subdomainSlug =
      host.endsWith(".ditours.pt") && host.split(".").length >= 3
        ? host.split(".")[0].toLowerCase()
        : null;
    const finalSlug = slug || subdomainSlug;
    if (finalSlug && finalSlug !== "www" && finalSlug !== "admin") {
      axios
        .get(`${API}/public/partner/${finalSlug}`)
        .then((r) => setPartnerCtx({ slug: finalSlug, company_name: r.data?.company_name || finalSlug }))
        .catch(() => setPartnerCtx(null));
    }
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Pre-fill the booking modal for logged-in clients with their saved personal data
  const clientPrefill = isClient && user
    ? {
        client_name: user.name || "",
        client_email: user.email || "",
        client_phone: user.phone || "",
        payment_method: user.last_payment_method || "mbway",
      }
    : null;

  const openBooking = useCallback((categoryKey = "executive", extraPrefill = null) => {
    setBookingCategory(categoryKey);
    setBookingPrefill(extraPrefill || clientPrefill);
    setBookingOpen(true);
  }, [clientPrefill]);

  return (
    <div className="dt-landing" data-testid="landing-page">
      <PartnerRibbon partnerCtx={partnerCtx} />

      <LandingHeader
        partnerCtx={partnerCtx}
        onBook={openBooking}
        onScrollTo={scrollTo}
      />

      <Hero onBook={openBooking} onScrollTo={scrollTo} />

      {isClient && (
        <QuickBookCard
          fleet={fleet}
          clientPrefill={clientPrefill}
          userName={user?.name}
          onSubmit={(prefill, categoryKey) => openBooking(categoryKey, prefill)}
        />
      )}

      <TrustBar />

      <ToursSection tours={tours} lang={lang} />

      <HowItWorks />

      <FleetSection fleet={fleet} onBook={openBooking} />

      <TestimonialsSection />

      <JoinSection onOpenJoin={setJoinOpen} />

      <FinalCTA onBook={openBooking} />

      <LandingFooter onScrollTo={scrollTo} />

      <AnimatePresence>
        {bookingOpen && (
          <BookingModal
            key="booking"
            categoryKey={bookingCategory}
            fleet={fleet}
            partnerCtx={partnerCtx}
            prefill={bookingPrefill}
            onClose={() => setBookingOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {joinOpen && (
          <JoinModal
            key="join"
            option={JOIN_OPTIONS.find((o) => o.key === joinOpen)}
            onClose={() => setJoinOpen(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
